import { collectCustomAddins } from "../utils/pageLoader";
import { CancelablePromise } from "../utils/cancelablePromise";
import * as logger from "../utils/logger";
import { getServerDate } from "../utils/storage/date";
import { getCurrentVehicle } from "../utils/device";
import Service from "../services/base";
import Api from "./api";
import Movement from "./sharedServices/movement";
import Sync from "../sync/sync";
import { DriveStore } from "../store/reducers";
import { DataActions } from "../store/data/actions";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { UserService } from "../utils/users";
import { createInsecureAddinsFilter, createUserAddinsAccessFilter, checkInternalAddinAccessFilter
} from "../utils/addinHelper";
import {DutyStatusLogService} from "../model/DutyStatusLogs";
import { getDatabaseNameFromPath } from "../utils/uri";
import serviceWorker from "../utils/serviceWorker";
import { updateAppCache } from "../utils/appcache";
import { trackedFetch } from "../utils/trackedFetch";
import { extend } from "../utils/vanilla";
import type { IFeatureFlagsService } from "./featureFlags";
import { SystemSettings, SYSTEM_SETTINGS_FIELDS, ISystemSettings } from "../store/systemSettings/types";
import { SystemSettingsActions } from "../store/systemSettings/actions";
import mainScheduler from "../utils/scheduler";
import _ from "underscore";
import storage from "../utils/storage/storage";
import { ErrorHandler } from "../utils/error.impl";
import type { IDutyStatusLog } from "../model/DutyStatusLog";
import idSerializer from "geotab-uuid";
import { getAllRuleSets, recreateRuleSetsList, type THosMalfunction, type THosOrigin, type THosStatus } from "hos-tools";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfilerFactory from "../utils/performanceProfiler/performanceProfilerFactory";
import { defaultInternalAddins } from "../constants/internalAddins";

const MAX_EXPECTED_LATENCY = 2000;
const MAX_CLOCKSYNC_ATTEMPTS = 3;
const CLOCKSYNC_TIMEOUT = 1000 * 30;
const APPCACHE_SOURCE_CODE_VERSION_REGEX = /# (.*) /;
const SERVICEWORKER_SOURCE_CODE_VERSION_REGEX = /drive-cache-(.*)\|/;

export interface IVersionInformation {
    server: string;
    goTalk: string;
    database: string;
    serverFlags: number;
    serverId: string;
    application: {
        full: string;
        branch: string;
        commit: string;
        build: string;
    };
}

/**
 * Defines the structure for an add-in that is in localstorage. Cached Add-Ins contain the page's raw HTML and if available, a translation dictionary.
 * */
export interface ICachedAddin {
    html: string;
    translation: object;
}

interface IAddinError {
    error: Error;
    message: string;
}

class AddinError extends Error implements IAddinError {

    readonly error: Error;
    constructor (message: string, e: Error) {
        super(message);
        this.error = e;
    }
}

class ContentLoadError extends Error {

    readonly status: number;
    constructor (message: string, status: number) {
        super(message);
        this.status = status;
    }
}

/**
 * Represents a collection of functions for requesting & storing SystemSettings,
 * Add-ins, software versions, and other information.
 */
export class DatabaseCache extends Service {

    constructor (
        private readonly api: Api,
        private readonly movement: Movement,
        private readonly sync: Sync,
        private readonly store: DriveStore,
        private readonly userService: UserService,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly featureFlagsService: IFeatureFlagsService,
        private readonly errorHandler: ErrorHandler
    ) {
        super();

        // Use the locally bundled hos-tools rulesets instead of fetching from the server.
        // Dispatch only when the store is empty (first login or post-logout); skip on scheduler ticks.
        this.populateRuleSetsFromLocal();
    }

    static setVersionInformation(store: DriveStore, versionInformation: IVersionInformation): void {
        if (versionInformation) {
            let serverVersion = versionInformation.application?.full || "";
            serverVersion && store.dispatch(DataActions.setDatabaseVersion(serverVersion));
            versionInformation.serverFlags !== undefined && store.dispatch(DataActions.setServerFlags(versionInformation.serverFlags));
        }
    }


    getSystemSettings(prefetchedData?: ISystemSettings[]) {
        return PerformanceProfilerFactory.getInstance(MeasureName.GetSystemSettings).measureAsync(() =>
            new CancelablePromise(resolve => {
                if (prefetchedData !== undefined) {
                    // Data was pre-fetched as part of a multiCall — skip the API request and process directly
                    this.processSystemSettingsData(prefetchedData, resolve);
                } else {
                    this.api.call("Get", {
                        typeName: "SystemSettings",
                        propertySelector: {
                            fields: SYSTEM_SETTINGS_FIELDS,
                            isIncluded: true
                        }
                    }, (data) => {
                        this.processSystemSettingsData(data, resolve);
                    }, (err) => {
                        logger.error(logger.Source.SYNC, `Failure getting system settings: ${ err.message }`, this.api?.getUser(), err);
                        resolve(this.store.getState().systemSettings);
                    });
                }
            }));
    }

    getLoginServerVersion(): CancelablePromise<string | null> {
        return new CancelablePromise(resolve => {
            this.api.serverCall(window.DRIVE_DEBUG?.SERVERCALL_TEST || "https://my.geotab.com", "GetVersion", {})
                .then((result: string) => {
                    resolve(result);
                })
                .catch(error => {
                    logger.error(logger.Source.API, `Failure getting login server information: ${ error.message }`, this.api?.getUser(), error);
                    resolve(null);
                });
        });
    }

    /**
     * Updates the source code version via Service Worker or AppCache and logs information
     * about the browser, and versions to Source.LAUNCHER
     * @returns A CancelablePromise that resolves when the source code version has been updated & logged
     */
    updateSourceCodeVersion(): CancelablePromise<void> {
        let database = getDatabaseNameFromPath();
        return new CancelablePromise<void>(resolve => {
            if (window.serviceWorkerEnabled) {
                return caches.keys()
                    .then(keys => {
                        if (keys && keys.length > 0) {
                            for (let i = 0; i < keys.length; i++) {
                                if (database ? keys[i].indexOf(database) > -1 : keys[i].indexOf("*")) {
                                    let version = SERVICEWORKER_SOURCE_CODE_VERSION_REGEX.exec(keys[i]);
                                    if (version && version[1]) {
                                        this.store.dispatch(DataActions.setSourceCodeVersion(version[1]));
                                        resolve();
                                    }
                                    break;
                                }
                            }
                        }
                        resolve();
                    })
                    .catch(error => {
                        logger.error(logger.Source.API, `Failure checking serviceworker: ${ error.message }`, this.api?.getUser(), error);
                        resolve();
                    });
            } else {
                return this.requestFile(`${ this.getServer() }/${ (database ? `${ database }/` : "") }drive/build/drive.appcache`)
                    .then(contents => {
                        let version = APPCACHE_SOURCE_CODE_VERSION_REGEX.exec(contents);
                        if (version && version[1]) {
                            this.store.dispatch(DataActions.setSourceCodeVersion(version[1]));
                            resolve();
                        }
                        resolve();
                    })
                    .catch(() => resolve());
            }
        }).then(async () => {
            logger.log(logger.Source.LAUNCHER, JSON.stringify({
                versions: this.errorHandler.getAppVersionInfo(),
                browser: await this.errorHandler.getBrowserInfo()
            }));
        });
    }

    refreshMyGeotabProperties(): CancelablePromise<IVersionInformation | null> {
        return new CancelablePromise(resolve => {
            this.api.callPromise("GetVersionInformation", {}, true)
                .then((result: IVersionInformation) => {
                    DatabaseCache.setVersionInformation(this.store, result);
                    resolve(result);
                })
                .catch(error => {
                    logger.error(logger.Source.API, `Failure getting server information: ${ error.message }`, this.api?.getUser(), error);
                    resolve(null);
                });
        });
    }

    checkSourceCodeUpdate() {
        this.refreshMyGeotabProperties()
            .then(() => {
                let serverVersion = this.store.getState().data.databaseVersion;
                let sourceCodeVersion = this.store.getState().data.sourceCodeVersion;
                if (serverVersion && (!sourceCodeVersion || serverVersion !== sourceCodeVersion)) {
                    serviceWorker
                        .update()
                        .catch(() => {
                            updateAppCache();
                        });
                }
            });
    }

    getClockDrift(): CancelablePromise<number> {
        let apiUser = this.api.getUser();
        let malfunctionLogs = this.dutyStatusLogService.getHOSLogs(true, apiUser ? apiUser.id : undefined, false, true, true);
        let latestTimingMalfunction = this.dutyStatusLogService.getLatestLog(malfunctionLogs, undefined, l => l.isTimingComplianceLog());
        let getCurrentDate = (): Date => {
            let serverDate = getServerDate();
            serverDate.setHours(0, 0, 0, 0);
            return serverDate;
        };
        let isNotInTimingMalfunction = !latestTimingMalfunction || latestTimingMalfunction.malfunction === "UserMalfunctionClear" || latestTimingMalfunction.getDate() < getCurrentDate();
        let checkedForMalfunction = false;
        let checkTimingCompliance = (drift: number) => {
            checkedForMalfunction = true;

            let tenMinutes = 600000;
            let currentVehicle = getCurrentVehicle();

            if (isNotInTimingMalfunction && Math.abs(drift) >= tenMinutes && currentVehicle && currentVehicle.id && currentVehicle.id !== "NoDeviceId") {
                let newLog: IDutyStatusLog = {
                    id: idSerializer.get(),
                    dateTime: getServerDate().toISOString(),
                    status: "TimingCompliance" as THosStatus,
                    malfunction: "Malfunction" as THosMalfunction,
                    device: { id: currentVehicle.id },
                    driver: { id: "NoUserId" },
                    origin: "Automatic" as THosOrigin
                };
                void this.sync.dutyStatusLogs.addItem(newLog, false);
            }
        };
        let getSystemTime = () => {
            return new CancelablePromise<string>((resolve, reject) => {
                this.api.call("GetSystemTimeUtc", {}, resolve, reject);
            });
        };

        let latency = Infinity;
        let attemptCount = 0;
        let clockDrift: number;
        let getClockDriftInRounds = () => {
            return new CancelablePromise<number>((resolve, reject) => {
                let setClockDriftAndCheckForMalfunction = () => {
                    this.store.dispatch(DataActions.setClockDrift(clockDrift));
                    !checkedForMalfunction && checkTimingCompliance(clockDrift);
                    resolve(clockDrift);
                };

                attemptCount++;

                // The "new Date()"s in this function should be so, and not "getServerDate()"
                let callStartTime = new Date().getTime();
                getSystemTime()
                .timeout(CLOCKSYNC_TIMEOUT)
                .then(result => {
                    let roundTripTime = new Date().getTime() - callStartTime;
                    if (roundTripTime < latency) {
                        latency = roundTripTime;

                        clockDrift = new Date(result).getTime() - callStartTime - (roundTripTime / 2);
                    }

                    if (latency > MAX_EXPECTED_LATENCY && attemptCount < MAX_CLOCKSYNC_ATTEMPTS) {
                        setTimeout(() => getClockDriftInRounds().then(resolve, reject), MAX_EXPECTED_LATENCY);
                    } else {
                        setClockDriftAndCheckForMalfunction();
                    }
                })
                .catch(err => {
                    if (attemptCount < MAX_CLOCKSYNC_ATTEMPTS) {
                        setTimeout(() => getClockDriftInRounds().then(resolve, reject), MAX_EXPECTED_LATENCY);
                    } else {
                        if (clockDrift) {
                            // We might have gotten a clock drift in one of our attempts, if so, this is still considered a success
                            setClockDriftAndCheckForMalfunction();
                        } else {
                            logger.error(logger.Source.SYNC, `Failure getting system time: ${ err.message || err }`, this.api?.getUser(), err);
                            reject(err);
                        }
                    }
                });
            });
        };

        return getClockDriftInRounds();
    }

    populateRuleSetsFromLocal() {
        this.store.dispatch(DataActions.setRuleSetsCache(recreateRuleSetsList(getAllRuleSets())));
    }

    destroy() {}

    setAddin(storageKey: string, addinToCache: ICachedAddin) {
        storage.set(storageKey, addinToCache, "ADDIN");
    }

    getCachedAddin(storageKey: string): ICachedAddin {
        return storage.get(storageKey, true, "ADDIN") || { html: "", translation: {} };
    }

    getAddins() {
        let settings = this.store.getState().systemSettings;
        let currentUser = this.userService.getCurrentUser();
        let userAcl: string[] = [];
        if (currentUser) {
            userAcl = extend(true, [], this.store.getState().data.acl[currentUser.id] || []);
        }

        if (settings && settings.customerPages) {
            let allowUnsignedAddIn = settings.allowUnsignedAddIn || false;
            return settings.customerPages
                .map(str => JSON.parse(str))
                .filter(addinJSON => createInsecureAddinsFilter(allowUnsignedAddIn, addinJSON))
                .filter(createUserAddinsAccessFilter(userAcl));
        }

        return [];
    }

    //Item is not typed to preserve legacy code, when noImplicitAny and strictPropertyInitialization gets turned on, this needs to be changed
    requestAndSave(item: any, successCallback?: (content: string) => void, errorCallback?: (err: Error) => void) {
        this.requestAddinFile(item.rawUrl)
            .then((addinHtml: string) => {
                let addinKey = `${ item.addinPath }${ item.path }`;
                let addinToCache: ICachedAddin = {
                    html: addinHtml,
                    translation: {}
                };

                // Load user language settings and translate if necessary. //
                let apiUserLanguage = this.api.getUser()?.language || "en";
                let token = this.api.getCurrentCredentials();

                if (!token) {
                    throw new Error("Couldn't load add-in files without credentials");
                }

                // Check to see if there is a translation JSON file available for the user's language setting.
                let path = `${ item.mainFileFolder }translations/${ apiUserLanguage }.json`;
                this.requestAddinFile(path)
                    .then((result: string) => {
                        // If there is, set the translation property for the addin.
                        addinToCache.translation = JSON.parse(result);
                        this.setAddin(addinKey, addinToCache);
                        successCallback && successCallback(addinHtml);
                    })
                    .catch(error => {
                        errorCallback && errorCallback(error);
                    });

                // If there is no translation available, that's fine as well.
                this.setAddin(addinKey, addinToCache);
                successCallback && successCallback(addinHtml);
            })
            .catch(e => {
                logger.log(logger.Source.ADDIN, `Error: failed to preload addin file: ${ item.url }`);
                errorCallback && errorCallback(e);
            });
    }

    /**
     * Leverages the Addin API to populate SystemSettings.customerPages with addin data.
     *
     * Fetches Add-ins from the Addin API and formats them to match the SystemSettings.customerPages
     * format. Since customerPages is a JSON string array of Add-in items, they can be swapped in directly.
     * @param settings SystemSettings value from the API
     */
    injectAddinsFromApi(settings: SystemSettings) {
        return new CancelablePromise(resolve => {
            this.api.call("Get", {
                typeName: "Addin",
                propertySelector: {
                    fields: ["id", "url", "status", "errorMessage", "configuration"],
                    isIncluded: true
                }
            }, (apiAddins) => {
                logger.log(logger.Source.ADDIN, `API Addins fetched: ${ apiAddins.length } addins`, this.api?.getUser());
                if (apiAddins.length) {
                    settings.customerPages = apiAddins.map(addin => {
                        if (addin.url) {
                            return JSON.stringify({ url: addin.url });
                        }
                        return JSON.stringify(addin.configuration);
                    });
                } else {
                    // Explicitly set `customerPages` to an empty array if there is none from the API, in case there is a disrepancy between the
                    // SystemSettings & Add-in API. (i.e. Add-in API is the sole source of truth in the case this workflow is enabled).
                    settings.customerPages = [];
                }
                resolve(settings);
            }, (error) => {
                logger.error(
                    logger.Source.SYNC,
                    `Failure downloading Add-ins from API: ${ error.message }. Continuing Add-in workflow using SS customerPages`,
                    this.api?.getUser(),
                    error
                );
                resolve(settings);
            });
        });
    }


    private processSystemSettingsData(data: ISystemSettings[], resolve: (value: ISystemSettings) => void) {
        let settings = new SystemSettings((data || [{}])[0]);

        const injectAddinsHelper = (settingsToInject) => {
            this.injectAddinScripts(settingsToInject).then((adjustedSettings) => {
                this.store.dispatch(SystemSettingsActions.setSystemSettings(adjustedSettings));
                mainScheduler.start("@featureFlags", this.api.getUser()?.name);
                logger.log(logger.Source.SYNC, `System settings updated`, this.api?.getUser());
                dispatcher.trigger(DispatcherEvents.SystemSettingsUpdate);

                // Restart tracking to check for customCode
                this.movement.trackMovement();

                resolve(adjustedSettings);
            }).catch(() => {
                this.movement.trackMovement();
                resolve(settings);
            });
        };

        this.injectAddinsFromApi(settings).then(injectAddinsHelper);
    }

    private getServer() {
        let credentials = this.api.getCurrentCredentials();
        let hostname = credentials ? credentials.server : window.location.host;

        return `https://${ hostname.replace(/\S*:\/\//, "").replace(/\/$/, "") }`;
    }

    private requestFile (url: string) {
        // it should be GET request for plain/text content for preventing CORS OPTIONS request: https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests
        if (typeof fetch !== "undefined") {
            let headers: IIdDictionary<string> = {
                "Content-Type": "text/plain"
            };
            if (this.userService.getCurrentUser()?.language) {
                headers["Accept-Language"] = this.userService.getCurrentUser()?.language || "en";
            }
            return trackedFetch(url, {
                headers
            })
            .then(r => r.text()) as CancelablePromise<string>;
        }

        return new CancelablePromise<string>((res, rej) => {
            const xhr = new XMLHttpRequest();

            xhr.open("GET", url, true);
            xhr.setRequestHeader("Content-Type", "text/plain");
            if (this.userService.getCurrentUser()?.language) {
                xhr.setRequestHeader("Accept-Language", this.userService.getCurrentUser()?.language || "en");
            }
            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        res(xhr.responseText);
                    } else {
                        rej(new ContentLoadError("Can't load content", xhr.status));
                    }
                }
            };

            xhr.timeout = 30000;
            xhr.ontimeout = () => { rej(new ContentLoadError("Timeout", 408)); };
            xhr.onerror = () => rej(new ContentLoadError("Can't load content", xhr.status || 0));

            xhr.send(null);
        });
    }


    /* eslint-disable @typescript-eslint/member-ordering */

    private requestAddinFile(url?: string) {
        let token = this.api.getCurrentCredentials();
        if (!token || !url) {
            return CancelablePromise.reject(new Error("Couldn't load add-in files - missing either credentials or url"));
        }

        if (url.indexOf("http") !== 0) {
            return this.requestFile(`${ this.getServer() }/${ token.credentials.database }/${ url }`);
        }

        return this.requestFile(url);
    }


    //Settings is not typed to preserve legacy code, when noImplicitAny and strictPropertyInitialization gets turned on, this needs to be changed
    private injectAddinScripts(settings: any) {
        let settingsClone = _.clone(settings);
        let makeAddinError = (e: Error, message?: string) => new AddinError(message || "Unknown addin's error", e);

        // Recursively parse addins if they are a URL reference
        let parseAddin = (addinStr: string) => {
            let downloadAddin = (addin: { url: string }): CancelablePromise => {
                const url = addin.url;
                return this.requestFile(url)
                    .then(jsonResult => {
                        try {
                            return parseAddin(jsonResult);
                        } catch (e) {
                            throw makeAddinError(e, `Can't parse manifest file for ${ url }`);
                        }
                    }, err => {
                        throw makeAddinError(err, `Can't download manifest for ${ url }`);
                    });
            };

            try {
                const addinObj = JSON.parse(addinStr);
                if ("url" in addinObj) {
                    return downloadAddin(addinObj)
                        .then(function (addin) {
                            addin.downloadUrl = addinObj.url;
                            return addin;
                        }).catch(err => {
                            // don't log unknown addin errors
                            if (err.message.indexOf("Unknown addin's error") === -1) {
                                logger.error(logger.Source.ADDIN, `Downloading add-in: ${ err.message }`, this.api?.getUser(), err);
                            }
                            return addinObj;
                        });
                }

                return Promise.resolve(addinObj);
            } catch (e) {
                return Promise.reject(makeAddinError(e));
            }
        };

        // Filter internal addins based on FeatureFlags
        let availableInternalAddins = defaultInternalAddins.filter(addin => {
            return this.featureFlagsService.hasFeatureFlag(addin.featureFlag);
        })
        .map(addinConfig => JSON.stringify(addinConfig));

        // Parse each addin (potentially just a URL ref to a full addin config)
        return Promise.all(settingsClone.customerPages.concat(availableInternalAddins).map(parseAddin)).then((addinConfigs: any[]) => {
            let currentUser = this.api.getUser();

            let userAcl: string[] = [];
            if (currentUser) {
                userAcl = extend(true, [], this.store.getState().data.acl[currentUser.id] || []);
            }

            let availableAddins = addinConfigs
                .filter(createUserAddinsAccessFilter(userAcl))
                .filter(addinJSON => createInsecureAddinsFilter(settingsClone.allowUnsignedAddIn || false, addinJSON));

            // Filter internal add-ins based on security IDs.
            availableAddins = availableAddins.filter(addin => {
                let internalAddinConfig = defaultInternalAddins.find(intAddin => intAddin.url.indexOf(addin.downloadUrl) > -1);
                if (internalAddinConfig) {
                    return checkInternalAddinAccessFilter(userAcl, internalAddinConfig.viewSecurityIdentifier);
                }
                return true;
            });

            // Download each of the addin items with menu DriveAppLink
            let addinItems = collectCustomAddins(availableAddins, currentUser);
            addinItems = addinItems.filter((addinConfig: { path?: string }) => addinConfig.path && addinConfig.path === "DriveAppLink/");
            //Item is not typed to preserve legacy code, when noImplicitAny and strictPropertyInitialization gets turned on, this needs to be changed
            addinItems.forEach((item: any) => this.requestAndSave(item));

            settingsClone.customerPages = availableAddins.map(addin => JSON.stringify(addin));
            return settingsClone;
        });
    }
    /* eslint-enable @typescript-eslint/member-ordering */
}